// src/pages/ROICalculator/constants.js

/**
 * ROI Calculator Configuration
 * All assumptions, data sources, and calculation parameters
 * Update this file when research changes
 */

// ============================================
// CALCULATION PARAMETERS
// ============================================

export const CALCULATION_CONFIG = {
    // Customer Service Parameters
    customerService: {
      averageResponseTime: 3, // minutes per message
      aiHandlingRate: 0.80, // 80% of queries AI can handle
      description: "Average time to respond to a customer message and AI's capability to handle queries",
      source: "IBM Global AI Adoption Index 2022 + HubSpot Customer Service Statistics 2023"
    },
  
    // Content Creation Parameters
    contentCreation: {
      baseWeeklyHours: 7.5, // hours per week
      aiReductionRate: 0.70, // 70% time reduction
      description: "Baseline time spent on content creation and AI's reduction capability",
      source: "Content Marketing Institute B2C Benchmarks Report 2023"
    },
  
    // Review Management Parameters
    reviewManagement: {
      responseTime: 8, // minutes per review
      aiAssistanceRate: 0.75, // 75% AI assistance
      description: "Time to craft thoughtful review responses and AI's assistance level",
      source: "BrightLocal Local Consumer Review Survey 2023"
    },
  
    // Labor Cost Parameters
    laborCosts: {
      partTimeHourlyRate: 18, // $/hour
      benefitsMultiplier: 1.25, // 25% for taxes/benefits
      description: "Average part-time customer service wage plus benefits/taxes",
      source: "U.S. Bureau of Labor Statistics 2023"
    },
  
    // Tool Cost Comparison
    toolCosts: {
      traditionalMonthly: 117, // $/month for separate tools
      revferPremium: 49, // $/month
      breakdown: {
        chatbot: 49,
        contentTool: 29,
        reviewManagement: 39
      },
      description: "Cost comparison between buying separate tools vs Revfer",
      source: "Market research: Intercom, Buffer, Podium pricing 2024"
    },
  
    // Revenue Impact Parameters
    revenueImpact: {
      ratingImpactPerStar: 0.06, // 6% per star (conservative)
      ratingImpactRange: [0.05, 0.09], // 5-9% range from research
      responseTimeConversionBoost: 0.10, // 10% improvement
      networkDiscoveryBoost: 0.08, // 8% potential increase
      description: "Revenue impact from rating improvements and faster response times",
      source: "Harvard Business School (Michael Luca) + HubSpot Research 2023"
    },
  
    // Smart Rating Estimation
    smartRatingBoost: {
      high: { threshold: 4.5, boost: 0.1 }, // already high
      good: { threshold: 4.0, boost: 0.3 }, // good ratings get boost
      moderate: { threshold: 3.5, boost: 0.4 }, // moderate ratings significant boost
      low: { threshold: 0, boost: 0.2 }, // low ratings limited boost
      description: "Expected Smart Rating improvement based on current traditional rating",
      source: "Revfer internal analysis + Yelp filtering data (25% fake reviews)"
    }, 
    // Social Media Content Generation Parameters
    socialMediaContent: {
        manualTimePerPost: 37.5, // minutes (average of 30-45)
        aiTimePerPost: 6, // minutes (average of 5-7)
        postsPerWeek: 5, // industry average
        timeReductionRate: 0.85, // 85% time reduction
        description: "Time to create quality social media posts manually vs. with AI assistance",
        source: "Buffer State of Social 2023 + Hootsuite Trends 2023"
    },
    
    socialMediaCosts: {
        schedulingTool: 20, // $/month (Buffer, Hootsuite basic)
        designTool: 13, // $/month (Canva Pro)
        captionAI: 49, // $/month (Copy.ai, Jasper basic)
        analytics: 29, // $/month (Sprout Social basic)
        totalWithoutRevfer: 111, // $/month for separate tools
        freelancerLow: 1000, // $/month (part-time freelancer)
        freelancerHigh: 2000, // $/month (experienced freelancer)
        agencyLow: 2000, // $/month (small agency)
        agencyHigh: 5000, // $/month (full-service agency)
        description: "Cost of social media tools and professional services",
        source: "Clutch Social Media Marketing Survey 2023, The Manifest 2023"
      },
    
      socialMediaImpact: {
        revenueBoostConservative: 0.10, // 10% revenue increase (conservative)
        revenueBoostAverage: 0.25, // 25% revenue increase (research average)
        revenueBoostHigh: 0.40, // 40% revenue increase (high performers)
        engagementMultiplier: 2.0, // 2x engagement with daily posting
        brandAwarenessIncrease: 0.80, // 80% brand awareness increase
        consumerBuyRate: 0.90, // 90% buy from brands they follow
        conversionAfterPositiveExperience: 0.78, // 78% will buy after positive social experience
        description: "Revenue and engagement impact of consistent, quality social media presence",
        source: "Sprout Social Index 2023, HubSpot Marketing Report 2023"
      },
  };
  
  // ============================================
  // INDUSTRY-SPECIFIC DATA
  // ============================================
  
  export const INDUSTRY_DATA = {
    restaurant: {
      label: 'Restaurant / Food Service',
      avgTransaction: 35,
      typicalMessages: 40,
      socialPostsPerWeek: 5, // NEW
      socialImportance: 'high', // NEW - visual content industry
      description: "Quick service and table service restaurants",
      source: "National Restaurant Association 2023"
    },
    retail: {
      label: 'Retail Store',
      avgTransaction: 75,
      typicalMessages: 25,
      socialPostsPerWeek: 5, // NEW
      socialImportance: 'high', // NEW - product showcasing
      description: "Physical retail locations",
      source: "Retail Industry Leaders Association 2023"
    },
    professional: {
      label: 'Professional Services',
      avgTransaction: 250,
      typicalMessages: 20,
      socialPostsPerWeek: 3, // NEW - less frequent but more thoughtful
      socialImportance: 'medium', // NEW
      description: "Lawyers, dentists, accountants, consultants",
      source: "Professional Services Industry Research 2023"
    },
    health: {
      label: 'Health & Wellness',
      avgTransaction: 80,
      typicalMessages: 30,
      socialPostsPerWeek: 5, // NEW
      socialImportance: 'high', // NEW - transformation content
      description: "Gyms, salons, spas, wellness centers",
      source: "International Health, Racquet & Sportsclub Association 2023"
    },
    home: {
      label: 'Home Services',
      avgTransaction: 350,
      typicalMessages: 15,
      socialPostsPerWeek: 3, // NEW
      socialImportance: 'medium', // NEW - before/after content
      description: "Plumbers, electricians, contractors, cleaners",
      source: "Home Service Industry Report 2023"
    },
    other: {
      label: 'Other Service Business',
      avgTransaction: 100,
      typicalMessages: 25,
      socialPostsPerWeek: 4, // NEW
      socialImportance: 'medium', // NEW
      description: "General service businesses",
      source: "Small Business Administration Industry Data"
    }
  };
  
  // ============================================
  // DATA SOURCES - Complete List
  // ============================================
  
  export const DATA_SOURCES = [
    {
      category: "AI Adoption & Impact",
      sources: [
        {
          name: "McKinsey & Company - The State of AI 2023",
          url: "https://www.mckinsey.com/capabilities/quantumblack/our-insights/the-state-of-ai",
          stats: [
            "50-60% of organizations have adopted AI",
            "Companies using AI report 6-10% revenue increases"
          ]
        },
        {
          name: "IBM Global AI Adoption Index 2022",
          url: "https://www.ibm.com/watson/resources/ai-adoption",
          stats: [
            "30-40% time savings on routine tasks",
            "6.9% average improvement in customer satisfaction"
          ]
        },
        {
          name: "Accenture - AI: Built to Scale",
          url: "https://www.accenture.com/us-en/insights/artificial-intelligence/ai-investments",
          stats: [
            "Customer service AI can reduce costs by up to 30%",
            "AI can boost profitability by 38% by 2035"
          ]
        },
        {
          name: "Forbes Advisor - AI in Business Statistics 2024",
          url: "https://www.forbes.com/advisor/business/ai-statistics/",
          stats: [
            "97% of business owners believe ChatGPT will benefit their business",
            "64% believe AI will increase productivity"
          ]
        }
      ]
    },
    {
      category: "Small Business Operations",
      sources: [
        {
          name: "U.S. Chamber of Commerce - Small Business Index",
          url: "https://www.uschamber.com/sbindex",
          stats: [
            "Small business owners work 50-60 hours per week",
            "49% cite finding and retaining employees as top challenge"
          ]
        },
        {
          name: "QuickBooks - Small Business Insights Report",
          url: "https://quickbooks.intuit.com/r/",
          stats: [
            "Average 120+ hours per month on administrative tasks",
            "68.1% of time spent on day-to-day operations"
          ]
        },
        {
          name: "U.S. Bureau of Labor Statistics",
          url: "https://www.bls.gov/oes/current/oes434051.htm",
          stats: [
            "Average customer service wage: $18/hour",
            "Benefits add approximately 25% to labor costs"
          ]
        }
      ]
    },
    {
      category: "Customer Service & Response Time",
      sources: [
        {
          name: "HubSpot - Customer Service Statistics",
          url: "https://blog.hubspot.com/service/customer-service-stats",
          stats: [
            "90% of customers rate immediate response as important",
            "60% define 'immediate' as 10 minutes or less"
          ]
        },
        {
          name: "Microsoft - State of Global Customer Service 2023",
          url: "https://dynamics.microsoft.com/",
          stats: [
            "90% of consumers expect 24/7 customer service",
            "58% have cut ties with a company due to poor service"
          ]
        },
        {
          name: "SuperOffice - Customer Service Benchmark Report",
          url: "https://www.superoffice.com/blog/customer-service-benchmark/",
          stats: [
            "Average email response time is 12 hours",
            "Only 36% of companies respond within 1 hour"
          ]
        }
      ]
    },
    {
      category: "Reviews & Trust",
      sources: [
        {
          name: "BrightLocal - Local Consumer Review Survey 2023",
          url: "https://www.brightlocal.com/research/local-consumer-review-survey/",
          stats: [
            "98% of consumers read online reviews for local businesses",
            "79% have encountered a fake review in the past year",
            "Average consumer reads 10 reviews before trusting a business"
          ]
        },
        {
          name: "Harvard Business School - Reviews, Reputation, Revenue",
          url: "https://www.hbs.edu/faculty/Pages/item.aspx?num=41233",
          stats: [
            "One-star increase in Yelp rating = 5-9% increase in revenue",
            "Independent restaurants more affected by reviews than chains"
          ]
        },
        {
          name: "Yelp - Consumer Alerts Report",
          url: "https://www.yelpblog.com/",
          stats: [
            "Yelp filters approximately 25% of reviews as potentially fake"
          ]
        }
      ]
    },
    {
      category: "Content Marketing",
      sources: [
        {
          name: "Content Marketing Institute - B2C Benchmarks 2023",
          url: "https://contentmarketinginstitute.com/",
          stats: [
            "82% of small businesses struggle with content creation",
            "Average 5-10 hours per week spent on content",
            "60% say producing engaging content is biggest challenge"
          ]
        },
        {
          name: "Hootsuite - Social Media Trends 2023",
          url: "https://www.hootsuite.com/research/social-trends",
          stats: [
            "90% of consumers buy from brands they follow on social media",
            "Small businesses that engage on social see 20-40% more revenue"
          ]
        }
      ]
    },
    {
      category: "Word-of-Mouth & Network Effects",
      sources: [
        {
          name: "Nielsen - Global Trust in Advertising Report",
          url: "https://www.nielsen.com/insights/",
          stats: [
            "92% of consumers trust recommendations from friends and family",
            "88% trust online reviews as much as personal recommendations"
          ]
        },
        {
          name: "McKinsey - Word-of-Mouth Marketing",
          url: "https://www.mckinsey.com/capabilities/growth-marketing-and-sales/our-insights/word-of-mouth-marketing",
          stats: [
            "Word-of-mouth generates 2x the sales of paid advertising",
            "20-50% of purchasing decisions influenced by word-of-mouth"
          ]
        }
      ]
    },
    {
        category: "Social Media & Content Marketing",
        sources: [
          {
            name: "Hootsuite - Social Media Trends Report 2023",
            url: "https://www.hootsuite.com/research/social-trends",
            stats: [
              "Small businesses spend 6-10 hours per week on social media",
              "Content creation consumes 3-4 hours weekly",
              "82% manage social media in-house"
            ]
          },
          {
            name: "Buffer - State of Social Media Report 2023",
            url: "https://buffer.com/state-of-social",
            stats: [
              "Average 8.5 hours per week on social media tasks",
              "Content creation takes 32% of social media time",
              "89% say social media is important to business"
            ]
          },
          {
            name: "Content Marketing Institute - B2C Benchmarks 2023",
            url: "https://contentmarketinginstitute.com/",
            stats: [
              "82% of small businesses struggle with content creation",
              "Average 5-10 hours per week on content",
              "60% cite engaging content as biggest challenge"
            ]
          },
          {
            name: "Sprout Social - The Sprout Social Index 2023",
            url: "https://sproutsocial.com/insights/index/",
            stats: [
              "90% of consumers buy from brands they follow",
              "Engaged brands see 20-40% more revenue",
              "78% will buy after positive social experience"
            ]
          },
          {
            name: "Clutch - Small Business Social Media Survey 2023",
            url: "https://clutch.co/agencies/social-media-marketing/resources",
            stats: [
              "Small businesses spend $200-$350/month on social tools",
              "Hiring social media manager: $3,000-$5,000/month",
              "Average ROI: $2.80 return per $1 spent"
            ]
          },
          {
            name: "Jasper AI & CMI - AI Content Survey 2023",
            url: "https://www.jasper.ai/",
            stats: [
              "AI reduces content creation time by 60-80%",
              "Businesses using AI create 3x more content",
              "67% say AI improves content quality"
            ]
          },
          {
            name: "SCORE - Small Business Social Media Survey",
            url: "https://www.score.org/",
            stats: [
              "52% can't maintain regular social posting",
              "Average quality post requires 30-45 minutes",
              "38% can't afford to hire social media help"
            ]
          },
          {
            name: "HubSpot - Social Media Marketing Report 2023",
            url: "https://blog.hubspot.com/marketing/social-media-marketing",
            stats: [
              "Businesses posting daily see 2x more engagement",
              "Video content generates 1200% more shares than text and images",
              "54% of social browsers use social media to research products",
              "80% of social media time should be engaging content"
            ]
          },
          {
            name: "Salesforce - State of Marketing Report 2023",
            url: "https://www.salesforce.com/resources/research-reports/state-of-marketing/",
            stats: [
              "51% of marketers already use AI for content creation",
              "AI users report 25% time savings on content tasks",
              "84% using AI say it helps personalize content at scale",
              "Marketers using AI create content 5x faster"
            ]
          },
          {
            name: "Constant Contact - Small Business Now Report",
            url: "https://www.constantcontact.com/",
            stats: [
              "46% of small business owners cite lack of time as biggest challenge",
              "34% struggle with creating quality content consistently",
              "29% don't know what to post about",
              "63% feel overwhelmed by number of social platforms",
              "Only 26% have documented social media strategy"
            ]
          },
        ]
      }
  ];
  
  // ============================================
  // CALCULATION ASSUMPTIONS (User-Facing)
  // ============================================
  
  export const ASSUMPTIONS = [
    {
      category: "Time Savings",
      items: [
        {
          assumption: "AI can handle 80% of customer service inquiries",
          rationale: "Based on IBM research showing AI effectively handles routine questions. Complex issues still require human attention.",
          conservative: true
        },
        {
          assumption: "Average response time per message is 3 minutes",
          rationale: "Includes reading message, researching answer, typing response, and any follow-up. Some messages faster, some slower.",
          conservative: true
        },
        {
          assumption: "Content creation time reduces by 70% with AI",
          rationale: "AI generates drafts, but humans still review, edit, and approve. Conservative estimate compared to 90%+ reduction possible.",
          conservative: true
        },
        {
          assumption: "Business operates 6 days per week",
          rationale: "Most small businesses open 6-7 days. We use 6 as conservative estimate.",
          conservative: true
        }
      ]
    },
    {
      category: "Cost Savings",
      items: [
        {
          assumption: "Part-time customer service costs $22.50/hour (including benefits)",
          rationale: "Based on BLS data: $18/hour base wage + 25% for taxes, insurance, and benefits. Does not include hiring costs, training, or turnover.",
          conservative: true
        },
        {
          assumption: "Separate tools cost $117/month combined",
          rationale: "Based on market research: Basic chatbot ($49), content scheduler ($29), review management ($39). Enterprise plans cost significantly more.",
          conservative: true
        }
      ]
    },
    {
      category: "Revenue Impact",
      items: [
        {
          assumption: "One-star rating increase generates 6% revenue increase",
          rationale: "Harvard research shows 5-9% range. We use 6% (middle of range) for conservative estimate. Impact varies by industry and competition.",
          conservative: true
        },
        {
          assumption: "Smart Rating increases by 0.1-0.4 stars depending on current rating",
          rationale: "Based on Yelp's data that 25% of reviews are filtered as fake. Businesses with more fake/low-credibility reviews see larger improvements.",
          conservative: true
        },
        {
          assumption: "Faster response time improves conversion by 10%",
          rationale: "HubSpot shows 90% want immediate responses. Conservative estimate of conversion improvement from instant AI vs. 12-hour human response time.",
          conservative: true
        },
        {
          assumption: "Network discovery provides 8% potential revenue boost",
          rationale: "McKinsey shows word-of-mouth generates 2x sales of paid ads. 8% is conservative estimate that requires network adoption in your area.",
          conservative: false,
          note: "Marked as 'potential' because requires local network growth"
        }
      ]
    },
    {
      category: "General Methodology",
      items: [
        {
          assumption: "All calculations use conservative estimates",
          rationale: "When research provides a range, we use the lower or middle value. Real results often exceed these calculations.",
          conservative: true
        },
        {
          assumption: "ROI varies by business type and implementation",
          rationale: "These calculations show averages. Your specific results depend on current efficiency, message volume, pricing, and engagement.",
          conservative: true
        },
        {
          assumption: "Time savings assume businesses were previously handling tasks manually",
          rationale: "If you already use some automation tools, your time savings will be net of current automation.",
          conservative: true
        }
      ]
    },
    {
        category: "Social Media Content Generation",
        items: [
          {
            assumption: "Manual social media post creation takes 30-45 minutes (average 37.5 minutes)",
            rationale: "Based on SCORE and Buffer research. Includes brainstorming, writing copy, finding/creating images, formatting, and adding hashtags. Some posts faster, some slower depending on complexity.",
            conservative: true
          },
          {
            assumption: "AI-assisted post creation takes 5-7 minutes (average 6 minutes)",
            rationale: "Includes reviewing AI-generated content, making edits, and approving. Initial business context setup (one-time, 10 minutes) not included in per-post calculation. Based on Jasper AI and Salesforce research.",
            conservative: true
          },
          {
            assumption: "Small businesses should post 3-5 times per week for effectiveness",
            rationale: "Based on Buffer and HubSpot research showing daily posting doubles engagement. We use 5 posts/week as standard, adjusting by industry (professional services: 3/week, visual businesses: 5-7/week).",
            conservative: true
          },
          {
            assumption: "AI maintains or improves content quality compared to manual creation",
            rationale: "Jasper AI research shows 67% of marketers report AI improves quality. AI-generated content shows similar or better engagement rates. We assume quality parity to be conservative.",
            conservative: true
          },
          {
            assumption: "Separate social media tools cost $111/month combined",
            rationale: "Based on market pricing: Scheduling ($20), Design ($13), Caption AI ($49), Analytics ($29). Many tools charge more for premium features. Does not include photo stock subscriptions.",
            conservative: true
          },
          {
            assumption: "Consistent social media presence increases revenue by 10%",
            rationale: "Sprout Social research shows 20-40% revenue increases for engaged brands. We use 10% as highly conservative estimate. Actual impact depends on industry, competition, and content quality.",
            conservative: true
          },
          {
            assumption: "Time saved is valued at owner's effective hourly rate",
            rationale: "When owner creates content personally, time has opportunity cost. We value at same rate as part-time labor ($50/hour effective rate including benefits). This is conservative as owner time often worth more.",
            conservative: true
          },
          {
            assumption: "Social media content generation includes posts, not engagement/responses",
            rationale: "Calculator focuses on content creation time only. Does not include time for responding to comments, DMs, or community management, which AI can also assist with but is calculated separately.",
            conservative: true
          },
          {
            assumption: "Social media posting frequency is user-defined",
            rationale: "Users can set their desired posting frequency (0-21 posts/week). We provide industry recommendations but allow full customization. Revenue impact scales with frequency: 5+ posts/week = full impact, 3-4 posts = 75% impact, 1-2 posts = 50% impact.",
            conservative: true
          }
        ]
      }
  ];
  
  // ============================================
  // SLIDER CONFIGURATIONS
  // ============================================
  
  export const SLIDER_CONFIG = {
    revenue: {
      min: 5000,
      max: 500000,
      step: 5000,
      default: 50000,
      label: 'Monthly Revenue',
      format: (val) => `$${val.toLocaleString()}`
    },
    messages: {
      min: 5,
      max: 200,
      step: 5,
      default: 30,
      label: 'Customer Messages per Day',
      format: (val) => val
    },
    reviews: {
      min: 0,
      max: 500,
      step: 5,
      default: 25,
      label: 'Total Reviews',
      format: (val) => val
    },
    rating: {
      min: 1.0,
      max: 5.0,
      step: 0.1,
      default: 4.2,
      label: 'Current Rating',
      format: (val) => `${val.toFixed(1)} ⭐`
    },
    socialPosts: {
      min: 0,
      max: 21,
      step: 1,
      default: 5,
      label: 'Social Media Posts per Week',
      format: (val) => val,
      marks: [
        { value: 0, label: 'None' },
        { value: 3, label: '3' },
        { value: 7, label: 'Daily' },
        { value: 14, label: '2x/day' },
        { value: 21, label: '3x/day' }
      ]
    }
  };
  
  // ============================================
  // HELPER FUNCTIONS
  // ============================================
  
  export const getIndustryLabel = (industry) => {
    return INDUSTRY_DATA[industry]?.label || 'Other Service Business';
  };
  
  export const getIndustryAvgTransaction = (industry) => {
    return INDUSTRY_DATA[industry]?.avgTransaction || 100;
  };
  
  export const getIndustryTypicalMessages = (industry) => {
    return INDUSTRY_DATA[industry]?.typicalMessages || 25;
  };
  export const getIndustrySocialPosts = (industry) => {
    return INDUSTRY_DATA[industry]?.socialPostsPerWeek || 4;
  };